import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { 
  Wine, 
  ChevronRight, 
  Trophy, 
  AlertCircle,
  CheckCircle2,
  XCircle,
  Star,
  BookOpen,
  ClipboardList,
  Beaker,
  Leaf,
  GlassWater,
  Zap,
  Clock,
  ClipboardCheck,
  ChevronDown,
  ChevronUp,
  RotateCcw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Mirrors the exact labels/descriptions an admin already sees when creating
   a test in TestCreationPage.jsx (TEST_TYPES) — shown here read-only so a
   staff member can see what the test actually covers without us inventing
   new copy. test_type/time_limit always come from the saved test record,
   never guessed. */
const TEST_TYPE_META = {
  full: { name: "Full Spec Test", desc: "Ingredients (brand & measurements), garnish, and glassware", icon: ClipboardList },
  ingredients: { name: "Ingredients Only", desc: "Focus on ingredient brands and measurements", icon: Beaker },
  presentation: { name: "Presentation Test", desc: "Garnish and glassware only — the visual elements", icon: Wine },
  garnish: { name: "Garnish Only", desc: "Test knowledge of garnishes for each cocktail", icon: Leaf },
  glassware: { name: "Glassware Only", desc: "Test knowledge of which glass to use", icon: GlassWater },
  speed: { name: "Speed Round", desc: "Beat the clock on every cocktail", icon: Zap },
};

// Resumable progress is scoped to this one access code and cleared the
// moment a real (non-practice) result is submitted, so a refresh/interruption
// never loses an in-progress attempt but a finished attempt never reappears.
const progressKey = (code) => `flashspecs_test_progress_${code}`;

export default function TakeTestPage() {
  const { accessCode } = useParams();
  const [assignmentData, setAssignmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Test state
  const [currentCocktailIndex, setCurrentCocktailIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [testStarted, setTestStarted] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Feedback state
  const [showFeedback, setShowFeedback] = useState(false);
  const [cocktailComplete, setCocktailComplete] = useState(false);
  
  // Score tracking
  const [totalScore, setTotalScore] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [perfectCocktails, setPerfectCocktails] = useState(0);
  const [isPractice, setIsPractice] = useState(false);
  const [selectedTestType, setSelectedTestType] = useState("full");
  // Phase 0.5: one stable id per attempt so a retried/duplicate submission
  // (e.g. a flaky network retry) can't create a second result or double-award XP.
  // Phase 5: reused across a refresh/resume (see resumeProgress) so a resumed
  // attempt still submits under the SAME idempotency key as the original.
  const clientSubmissionIdRef = useRef(crypto.randomUUID());
  // Review accordion on the final results screen (local data only — no
  // extra authenticated endpoint call, since this access-code flow has no
  // login session to call one with).
  const [expandedReviewCocktail, setExpandedReviewCocktail] = useState(null);
  // A saved-but-not-yet-applied in-progress attempt found in sessionStorage,
  // offered on the start screen as "Resume" vs "Start Over".
  const [resumableProgress, setResumableProgress] = useState(null);

  useEffect(() => {
    fetchAssignment();
    try {
      const saved = sessionStorage.getItem(progressKey(accessCode));
      if (saved) setResumableProgress(JSON.parse(saved));
    } catch {
      // corrupted/unavailable storage — just start fresh, no resume offered
    }
  }, [accessCode]);

  const fetchAssignment = async () => {
    try {
      const response = await axios.get(`${API}/test-assignments/by-code/${accessCode}`);
      setAssignmentData(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setError("Test not found or link is invalid");
      } else {
        setError("Failed to load test");
      }
    } finally {
      setLoading(false);
    }
  };

  // Persist progress on every change while a real (non-practice) attempt is
  // underway, so a refresh/tab-close mid-test can be resumed instead of
  // silently losing answers. Practice runs are never saved — they're
  // explicitly disposable (see the start screen copy). showFeedback and
  // cocktailComplete are included so a resumed question that was already
  // checked (and had its correct answer revealed) reopens in the SAME
  // revealed/locked state rather than as an editable, un-checked question —
  // otherwise a refresh right after seeing "Incorrect — correct answer: X"
  // would let the answer be silently switched to X on resume.
  useEffect(() => {
    if (!testStarted || testCompleted || isPractice) return;
    try {
      sessionStorage.setItem(progressKey(accessCode), JSON.stringify({
        currentCocktailIndex, currentQuestionIndex, answers, totalScore,
        correctAnswers, perfectCocktails, selectedTestType,
        showFeedback, cocktailComplete,
        clientSubmissionId: clientSubmissionIdRef.current,
      }));
    } catch {
      // storage unavailable — resume just won't be offered, test still works
    }
  }, [testStarted, testCompleted, isPractice, currentCocktailIndex, currentQuestionIndex, answers, totalScore, correctAnswers, perfectCocktails, selectedTestType, showFeedback, cocktailComplete, accessCode]);

  const resumeSavedProgress = () => {
    if (!resumableProgress) return;
    setCurrentCocktailIndex(resumableProgress.currentCocktailIndex || 0);
    setCurrentQuestionIndex(resumableProgress.currentQuestionIndex || 0);
    setAnswers(resumableProgress.answers || {});
    setTotalScore(resumableProgress.totalScore || 0);
    setCorrectAnswers(resumableProgress.correctAnswers || 0);
    setPerfectCocktails(resumableProgress.perfectCocktails || 0);
    setSelectedTestType(resumableProgress.selectedTestType || (test?.test_type || "full"));
    setShowFeedback(Boolean(resumableProgress.showFeedback));
    setCocktailComplete(Boolean(resumableProgress.cocktailComplete));
    clientSubmissionIdRef.current = resumableProgress.clientSubmissionId || clientSubmissionIdRef.current;
    setIsPractice(false);
    setTestStarted(true);
    setResumableProgress(null);
  };

  const discardSavedProgress = () => {
    try { sessionStorage.removeItem(progressKey(accessCode)); } catch { /* no-op */ }
    setResumableProgress(null);
  };

  const assignment = assignmentData?.assignment;
  const test = assignmentData?.test;
  const cocktails = assignmentData?.cocktails || [];
  const cocktailQuestions = assignmentData?.cocktail_questions || {};
  
  // Get staff info from assignment
  const staffId = assignment?.staff_id;
  const staffName = assignment?.staff_name;

  // The test's actual configured type (never guessed) — this is the only
  // type whose question set was ever generated for the test-taker to see.
  const configuredType = test?.test_type || "full";
  const typeMeta = TEST_TYPE_META[configuredType] || TEST_TYPE_META.full;
  // The "take the full set, or just the ingredients portion of it" choice
  // only makes sense when the test was itself configured as "full" — for
  // any other configured type (garnish/glassware/presentation/speed) the
  // question set is already scoped server-side, so offering a narrower
  // client-side filter on top of it could silently produce zero questions.
  const showTypeToggle = configuredType === "full";

  useEffect(() => {
    if (test && !resumableProgress) setSelectedTestType(configuredType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [test?.id]);

  // Filter questions based on selected test type. Each question keeps its
  // ORIGINAL index from the full (unfiltered) list as `_origIndex` — this is
  // the index the backend's build_test_question_map() (and therefore
  // _recompute_mcq_score) actually keys answers by for this cocktail. If we
  // used the filtered array's own position instead, a narrowed view (e.g.
  // "Ingredients & Measurements" on a Full Spec test) would submit answers
  // under the WRONG keys and get scored against different questions server-
  // side than the ones the staff member actually saw and answered.
  const filteredQuestions = useMemo(() => {
    if (!cocktailQuestions || Object.keys(cocktailQuestions).length === 0) return {};
    if (!showTypeToggle || selectedTestType === "full") {
      const indexed = {};
      for (const [cid, data] of Object.entries(cocktailQuestions)) {
        indexed[cid] = { ...data, questions: (data.questions || []).map((q, idx) => ({ ...q, _origIndex: idx })) };
      }
      return indexed;
    }
    // Ingredients only: keep measurement and brand questions
    const filtered = {};
    for (const [cid, data] of Object.entries(cocktailQuestions)) {
      const qs = (data.questions || [])
        .map((q, idx) => ({ ...q, _origIndex: idx }))
        .filter(q => q.type === "measurement" || q.type === "brand");
      if (qs.length > 0) {
        filtered[cid] = { ...data, questions: qs };
      }
    }
    return filtered;
  }, [cocktailQuestions, selectedTestType, showTypeToggle]);
  
  // Get current cocktail data from filtered questions
  const cocktailIds = Object.keys(filteredQuestions);
  const currentCocktailId = cocktailIds[currentCocktailIndex];
  const currentCocktailData = filteredQuestions[currentCocktailId];
  const currentQuestions = currentCocktailData?.questions || [];
  const currentQuestion = currentQuestions[currentQuestionIndex];
  
  const totalCocktails = cocktailIds.length;
  const totalQuestionsInCocktail = currentQuestions.length;
  const totalQuestionsOverall = cocktailIds.reduce((sum, cid) => sum + (filteredQuestions[cid]?.questions?.length || 0), 0);
  const questionsBeforeCurrentCocktail = cocktailIds.slice(0, currentCocktailIndex).reduce((sum, cid) => sum + (filteredQuestions[cid]?.questions?.length || 0), 0);
  const overallQuestionNumber = questionsBeforeCurrentCocktail + currentQuestionIndex + 1;

  const startTest = (forceFull) => {
    // The server's authoritative recompute always scores a "full" configured
    // test against its COMPLETE question set, regardless of what test_type
    // label the client submits — so narrowing to "Ingredients &
    // Measurements" and then taking the OFFICIAL Start Test would silently
    // record every unseen garnish/glassware/method question as wrong. That
    // narrower view is safe only for Practice (never submitted); the real
    // Start Test always runs the full set so the final score is accurate.
    // Resets to the test's own CONFIGURED type (not a literal "full") so a
    // garnish/glassware/presentation/speed test — where the toggle is
    // hidden and selectedTestType was never anything but configuredType
    // anyway — still records its own real test_type, not "full".
    if (forceFull) setSelectedTestType(configuredType);
    setTestStarted(true);
  };

  const handleAnswer = (answer) => {
    const key = `${currentCocktailId}_${currentQuestion._origIndex}`;
    setAnswers(prev => ({
      ...prev,
      [key]: answer
    }));
  };

  const submitAnswer = () => {
    const key = `${currentCocktailId}_${currentQuestion._origIndex}`;
    // Prevent double-submit for the same question
    if (showFeedback) return;
    
    const userAnswer = answers[key];
    const isCorrect = userAnswer?.toLowerCase() === currentQuestion.correct_answer?.toLowerCase();
    
    if (isCorrect) {
      setCorrectAnswers(prev => prev + 1);
      setTotalScore(prev => prev + 1);
    }
    
    setShowFeedback(true);
  };

  const nextQuestion = () => {
    setShowFeedback(false);
    
    if (currentQuestionIndex < totalQuestionsInCocktail - 1) {
      // Move to next question in same cocktail
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // Finished all questions for this cocktail - show cocktail summary
      checkCocktailComplete();
    }
  };

  const checkCocktailComplete = () => {
    // Check if all questions for this cocktail were answered correctly
    let allCorrect = true;
    for (let i = 0; i < totalQuestionsInCocktail; i++) {
      const key = `${currentCocktailId}_${currentQuestions[i]._origIndex}`;
      const userAnswer = answers[key];
      const question = currentQuestions[i];
      if (userAnswer?.toLowerCase() !== question.correct_answer?.toLowerCase()) {
        allCorrect = false;
        break;
      }
    }
    
    if (allCorrect) {
      setPerfectCocktails(prev => prev + 1);
      setTotalScore(prev => prev + 5); // Bonus points for perfect cocktail
    }
    
    setCocktailComplete(true);
  };

  const nextCocktail = () => {
    if (currentCocktailIndex < totalCocktails - 1) {
      setCocktailComplete(false);
      setCurrentQuestionIndex(0);
      setCurrentCocktailIndex(prev => prev + 1);
    } else {
      // Test complete — leave this cocktail's summary screen in place (don't
      // reset cocktailComplete/currentQuestionIndex) until the submission
      // actually succeeds, so a network failure re-shows "Finish Test" to
      // retry instead of silently reopening question 1 for re-answering
      // (which would double-count the live score display).
      submitTest();
    }
  };

  // Single source of truth for score/percentage from a given answers map —
  // used by submitTest (what's sent to the server) and the results screen
  // (what's displayed), so they can never drift apart from each other.
  const computeScoreFromAnswers = (ans) => {
    let correct = 0;
    let perfect = 0;
    for (const cid of cocktailIds) {
      const cData = filteredQuestions[cid];
      if (!cData) continue;
      const qs = cData.questions || [];
      let cocktailAllCorrect = qs.length > 0;
      for (const q of qs) {
        const key = `${cid}_${q._origIndex}`;
        if (ans[key]?.toLowerCase() === q.correct_answer?.toLowerCase()) {
          correct++;
        } else {
          cocktailAllCorrect = false;
        }
      }
      if (cocktailAllCorrect) perfect++;
    }
    const score = correct + perfect * 5;
    const totalQuestions = cocktailIds.reduce((sum, cid) => sum + (filteredQuestions[cid]?.questions?.length || 0), 0);
    const totalPossible = totalQuestions + (cocktailIds.length * 5);
    const percentage = totalPossible > 0 ? (score / totalPossible) * 100 : 0;
    return { correct, perfect, score, totalPossible, percentage };
  };

  const submitTest = async () => {
    setSubmitting(true);
    
    try {
      // Recompute scores from answers to prevent any state drift
      const { correct: computedCorrect, perfect: computedPerfect, score: computedScore, totalPossible: computedTotalPossible, percentage: computedPercentage } = computeScoreFromAnswers(answers);

      // Save test result — skip for practice mode. The backend derives staff
      // identity from the access_code and authoritatively recomputes the
      // score/percentage itself (Phase 0.5) — the values sent below are for
      // backward-compatible display only, not trusted as the official record.
      if (!isPractice) {
        const resultResponse = await axios.post(`${API}/test-results`, {
          staff_id: staffId,
          staff_name: staffName,
          test_id: test.id,
          test_name: test.name,
          test_type: selectedTestType,
          score: computedScore,
          total_possible: computedTotalPossible,
          percentage: computedPercentage,
          answers: answers,
          correct_answers: computedCorrect,
          perfect_cocktails: computedPerfect,
          access_code: accessCode,
          client_submission_id: clientSubmissionIdRef.current
        });
        // XP is now awarded server-side (inside the call above) from the
        // authoritative recomputed score — no separate client-triggered call.
        if (!resultResponse?.data?.xp_result) {
          console.warn("XP award unavailable for this submission");
        }
      }
      
      setCocktailComplete(false);
      setCurrentQuestionIndex(0);
      setTestCompleted(true);
      try { sessionStorage.removeItem(progressKey(accessCode)); } catch { /* no-op */ }
      toast.success(isPractice ? "Practice complete!" : "Test submitted!");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to submit test — tap Finish Test to try again");
    } finally {
      setSubmitting(false);
    }
  };

  // Get results for current cocktail (for feedback display)
  const getCocktailResults = () => {
    const results = [];
    for (let i = 0; i < totalQuestionsInCocktail; i++) {
      const question = currentQuestions[i];
      const key = `${currentCocktailId}_${question._origIndex}`;
      const userAnswer = answers[key];
      results.push({
        question: question.question_text,
        type: question.type,
        userAnswer,
        correctAnswer: question.correct_answer,
        isCorrect: userAnswer?.toLowerCase() === question.correct_answer?.toLowerCase()
      });
    }
    return results;
  };

  // Same per-cocktail breakdown as getCocktailResults(), but across every
  // cocktail in the attempt — used by the "Review Your Answers" accordion on
  // the final results screen. Built entirely from local state (no extra
  // authenticated endpoint call), since this access-code flow has no login
  // session to make one with.
  const getAllCocktailResults = () => {
    return cocktailIds.map(cid => {
      const cData = filteredQuestions[cid];
      const qs = cData?.questions || [];
      const questions = qs.map((q) => {
        const key = `${cid}_${q._origIndex}`;
        const userAnswer = answers[key];
        return {
          question: q.question_text,
          type: q.type,
          userAnswer,
          correctAnswer: q.correct_answer,
          isCorrect: userAnswer?.toLowerCase() === q.correct_answer?.toLowerCase()
        };
      });
      return {
        cocktailId: cid,
        cocktailName: cData?.cocktail_name || "Cocktail",
        questions,
        wrongCount: questions.filter(q => !q.isCorrect).length
      };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-[hsl(var(--muted-foreground))]">Loading test...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Unable to Load Test</h2>
            <p className="text-[hsl(var(--muted-foreground))]">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Results Screen
  if (testCompleted) {
    // Recompute from answers for accurate display — same helper used to
    // build the actual submitted payload, so the staff member never sees a
    // different score than what was recorded.
    const { correct: displayCorrect, perfect: displayPerfect, score: displayScore, percentage } = computeScoreFromAnswers(answers);
    // No pass mark is configured anywhere in this test's setup — these are
    // descriptive performance tiers (same 80/60 bands already used across
    // the app's results views), never an invented official pass/fail status.
    const tier = percentage >= 80
      ? { label: "Excellent", text: "text-green-500", border: "border-green-500", bg: "bg-green-500/10" }
      : percentage >= 60
      ? { label: "Good", text: "text-[#C36A2F]", border: "border-[#C36A2F]", bg: "bg-[#C36A2F]/10" }
      : { label: "Needs Review", text: "text-red-500", border: "border-red-500", bg: "bg-red-500/10" };
    // Shown for Practice too — unscored and never saved, but reviewing what
    // was right/wrong is still useful feedback from that run.
    const allResults = getAllCocktailResults();
    // Outer list stays open while any single cocktail ("all-N") is expanded.
    const reviewListOpen = expandedReviewCocktail === "all" || (typeof expandedReviewCocktail === "string" && expandedReviewCocktail.startsWith("all-"));

    return (
      <div className="min-h-screen bg-[hsl(var(--background))] p-4 md:p-8 pb-24">
        <div className="max-w-2xl mx-auto space-y-6">
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-8 text-center">
              <Trophy size={64} className={`mx-auto mb-4 ${tier.text}`} />
              <h1 className="text-3xl font-bold mb-2" data-testid="test-complete-title">
                {isPractice ? "Practice Complete!" : "Test Complete!"}
              </h1>
              {isPractice && (
                <p className="text-sm text-[hsl(var(--primary))] mb-1" data-testid="practice-not-saved-note">This was a practice run — not saved, no XP earned</p>
              )}
              {!isPractice && (
                <p className="text-sm text-[hsl(var(--primary))] mb-1" data-testid="formal-recorded-note">Formal assessment — recorded against your assigned test</p>
              )}
              <p className="text-[hsl(var(--muted-foreground))] mb-6">{staffName}</p>

              <div className={`inline-block px-8 py-4 rounded-xl border-2 ${tier.border} ${tier.bg}`}>
                <p className={`text-5xl font-bold ${tier.text}`} data-testid="final-score">
                  {displayScore} pts
                </p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  {displayCorrect} correct answers &bull; {displayPerfect} perfect cocktails
                </p>
                <p className={`text-sm font-semibold mt-1 ${tier.text}`} data-testid="score-tier">{tier.label} &bull; {percentage.toFixed(0)}%</p>
              </div>
            </CardContent>
          </Card>

          {!isPractice && (
            <p className="text-center text-sm text-[hsl(var(--muted-foreground))]" data-testid="results-saved-note">
              Your score, answers and XP have been saved to your training record.
            </p>
          )}

          {allResults.length > 0 && (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardContent className="p-0">
                <button
                  onClick={() => setExpandedReviewCocktail(reviewListOpen ? null : "all")}
                  className="w-full flex items-center justify-between p-4 text-left min-h-[48px]"
                  data-testid="toggle-review-section"
                >
                  <span className="font-bold flex items-center gap-2"><ClipboardCheck size={18} className="text-[hsl(var(--primary))]" /> Review Your Answers</span>
                  {reviewListOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {reviewListOpen && (
                  <div className="px-4 pb-4 space-y-2" data-testid="review-cocktails-list">
                    {allResults.map((r, cIdx) => {
                      const isOpen = expandedReviewCocktail === `all-${cIdx}`;
                      const allCorrect = r.wrongCount === 0;
                      return (
                        <div key={r.cocktailId} className="rounded-lg border border-[hsl(var(--border))] overflow-hidden">
                          <button
                            onClick={() => setExpandedReviewCocktail(isOpen ? "all" : `all-${cIdx}`)}
                            className="w-full flex items-center justify-between p-3 bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--accent))] transition-colors text-left min-h-[44px]"
                            data-testid={`review-cocktail-${cIdx}`}
                          >
                            <div className="flex items-center gap-2">
                              {allCorrect ? <CheckCircle2 size={16} className="text-green-500 shrink-0" /> : <XCircle size={16} className="text-red-500 shrink-0" />}
                              <span className="font-medium text-sm">{r.cocktailName}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {r.wrongCount > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded bg-red-500/15 text-red-400">{r.wrongCount} wrong</span>
                              )}
                              {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </div>
                          </button>
                          {isOpen && (
                            <div className="p-3 space-y-2 bg-[hsl(var(--card))]" data-testid={`review-questions-${cIdx}`}>
                              {r.questions.map((q, qIdx) => (
                                <div key={qIdx} className={`p-2.5 rounded-lg text-sm ${q.isCorrect ? "bg-green-500/5 border border-green-500/20" : "bg-red-500/5 border border-red-500/20"}`}>
                                  <p className="text-[hsl(var(--muted-foreground))] text-xs mb-1.5">{q.question}</p>
                                  {q.isCorrect ? (
                                    <div className="flex items-center gap-1.5">
                                      <CheckCircle2 size={13} className="text-green-500 shrink-0" />
                                      <span className="text-green-400 font-medium">{q.correctAnswer}</span>
                                    </div>
                                  ) : (
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-1.5">
                                        <XCircle size={13} className="text-red-500 shrink-0" />
                                        <span className="text-red-400 line-through">{q.userAnswer || "No answer"}</span>
                                      </div>
                                      <div className="flex items-center gap-1.5 pl-5">
                                        <span className="text-xs text-[hsl(var(--muted-foreground))]">Correct:</span>
                                        <span className="text-green-400 font-medium">{q.correctAnswer}</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {test?.id && (
              <>
                <Link to={`/study/${test.id}`}>
                  <Button
                    variant="outline"
                    className="border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 min-h-[48px]"
                    data-testid="study-flashcards-btn"
                  >
                    <BookOpen size={18} className="mr-2" />
                    Study Flashcards
                  </Button>
                </Link>
                <Link to={`/flashspecs/study/${test.id}`}>
                  <Button
                    variant="outline"
                    className="border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 min-h-[48px]"
                    data-testid="flashspecs-btn"
                  >
                    <BookOpen size={18} className="mr-2" />
                    FlashSpecs
                  </Button>
                </Link>
              </>
            )}
            <Link to="/my-dashboard">
              <Button
                className="bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90 min-h-[48px]"
                data-testid="return-dashboard-btn"
              >
                Return to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Cocktail Summary Screen (after completing all questions for a cocktail)
  if (cocktailComplete) {
    const cocktailResults = getCocktailResults();
    const correctInCocktail = cocktailResults.filter(r => r.isCorrect).length;
    const isPerfect = correctInCocktail === totalQuestionsInCocktail;
    
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] p-4 md:p-8 pb-24">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Score Progress Bar */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--secondary))]">
            <div className="flex items-center gap-2">
              <Star className="text-[hsl(var(--primary))]" size={20} />
              <span className="font-bold text-lg">{totalScore} pts</span>
            </div>
            <span className="text-sm text-[hsl(var(--muted-foreground))]">
              Cocktail {currentCocktailIndex + 1} of {totalCocktails}
            </span>
          </div>

          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6 text-center">
              {isPerfect ? (
                <>
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="text-green-500" size={32} />
                  </div>
                  <h2 className="text-2xl font-bold text-green-500 mb-2">Perfect!</h2>
                  <p className="text-[hsl(var(--primary))] font-semibold">+5 bonus points</p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold">{correctInCocktail}/{totalQuestionsInCocktail}</span>
                  </div>
                  <h2 className="text-xl font-bold mb-2">{currentCocktailData?.cocktail_name}</h2>
                </>
              )}
            </CardContent>
          </Card>

          {/* Results Breakdown */}
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="text-base">{currentCocktailData?.cocktail_name} - Results</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {cocktailResults.map((result, idx) => (
                <div
                  key={`${result.type}-${idx}`}
                  className={`p-3 rounded-lg ${
                    result.isCorrect ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1 capitalize">{result.type}</p>
                      <p className="text-sm">{result.question}</p>
                    </div>
                    {result.isCorrect ? (
                      <CheckCircle2 className="text-green-500 shrink-0" size={20} />
                    ) : (
                      <XCircle className="text-red-500 shrink-0" size={20} />
                    )}
                  </div>
                  {!result.isCorrect && (
                    <div className="mt-2 pt-2 border-t border-red-500/20">
                      <p className="text-xs text-red-400">Your answer: {result.userAnswer}</p>
                      <p className="text-xs text-green-400">Correct: {result.correctAnswer}</p>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          <Button
            onClick={nextCocktail}
            disabled={submitting}
            className="w-full min-h-[48px] btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
            data-testid="next-cocktail-btn"
          >
            {currentCocktailIndex < totalCocktails - 1 ? (
              <>Next Cocktail <ChevronRight size={18} className="ml-1" /></>
            ) : (
              submitting ? "Submitting..." : "Finish Test"
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Start Screen
  if (!testStarted) {
    const TEST_TYPE_OPTIONS = [
      { value: "full", label: "Full Spec Test", desc: "Ingredients, measurements, garnish, glass, method & ice" },
      { value: "ingredients", label: "Ingredients & Measurements", desc: "Brand and measurement questions only" },
    ];
    const configuredCocktailIds = Object.keys(cocktailQuestions);
    const configuredQuestionCount = configuredCocktailIds.reduce((s, cid) => s + (cocktailQuestions[cid]?.questions?.length || 0), 0);
    const TypeIcon = typeMeta.icon;

    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md w-full">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <Wine size={48} className="mx-auto text-[hsl(var(--primary))] mb-4" />
              <h1 className="text-2xl font-bold mb-2" data-testid="test-name-heading">{test?.name}</h1>
              {test?.description && (
                <p className="text-[hsl(var(--muted-foreground))]">{test.description}</p>
              )}

              <div className="mt-4 p-4 rounded-lg bg-[hsl(var(--secondary))]">
                <p className="font-medium text-lg text-[hsl(var(--primary))]">
                  Welcome, {staffName}!
                </p>
              </div>
            </div>

            {resumableProgress && (
              <div className="mb-6 p-4 rounded-lg border-2 border-dashed border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" data-testid="resume-banner">
                <p className="text-sm font-semibold mb-1">You have an attempt in progress</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Pick up where you left off, or start over and discard that progress.</p>
                <div className="flex gap-2">
                  <Button onClick={resumeSavedProgress} data-testid="resume-test-btn" className="flex-1 min-h-[44px] bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90">
                    <RotateCcw size={16} className="mr-1.5" /> Resume
                  </Button>
                  <Button onClick={discardSavedProgress} data-testid="discard-progress-btn" variant="outline" className="flex-1 min-h-[44px]">
                    Start Over
                  </Button>
                </div>
              </div>
            )}

            {/* What this test covers — read-only, exactly as configured by the admin */}
            <div className="mb-4 p-4 rounded-lg bg-[hsl(var(--secondary))] space-y-2" data-testid="test-info-panel">
              <div className="flex items-center gap-2">
                <TypeIcon size={18} className="text-[hsl(var(--primary))] shrink-0" />
                <div>
                  <p className="text-sm font-bold">{typeMeta.name}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">{typeMeta.desc}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))] pt-1 border-t border-[hsl(var(--border))]">
                <ClipboardList size={14} className="shrink-0" />
                <span data-testid="test-question-count">{configuredCocktailIds.length} cocktail{configuredCocktailIds.length === 1 ? "" : "s"} &bull; {configuredQuestionCount} question{configuredQuestionCount === 1 ? "" : "s"}</span>
              </div>
              {test?.time_limit && (
                <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]" data-testid="timed-indicator">
                  <Clock size={14} className="shrink-0" />
                  <span>Timed — {test.time_limit} seconds per cocktail</span>
                </div>
              )}
            </div>

            {/* Test Type Selector — only offered when the test was itself
                configured as "Full Spec", since any other configured type's
                question set is already scoped server-side. */}
            {showTypeToggle && (
              <div className="space-y-2 mb-6">
                <p className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] font-semibold">Select Test Type</p>
                {TEST_TYPE_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setSelectedTestType(opt.value)}
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all min-h-[48px] ${
                      selectedTestType === opt.value
                        ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10'
                        : 'border-[hsl(var(--border))] bg-[hsl(var(--secondary))] hover:border-[hsl(var(--primary))]/40'
                    }`}
                    data-testid={`test-type-${opt.value}`}
                  >
                    <p className={`text-sm font-bold ${selectedTestType === opt.value ? 'text-[hsl(var(--primary))]' : ''}`}>{opt.label}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{opt.desc}</p>
                  </button>
                ))}
                {selectedTestType !== "full" && (
                  <p className="text-xs text-[hsl(var(--muted-foreground))] pt-1" data-testid="ingredients-toggle-practice-only-note">
                    This narrower view is for Practice only — Start Test always covers the full test so your official score is accurate.
                  </p>
                )}
              </div>
            )}

            <div className="space-y-1.5 text-sm text-[hsl(var(--muted-foreground))] text-center mb-4" data-testid="scoring-rules">
              <p className="text-[hsl(var(--primary))]">+1 point per correct answer</p>
              <p className="text-[hsl(var(--primary))]">+5 bonus for a fully correct cocktail</p>
              <p className="text-xs">No pass mark is set for this test — your score is recorded for review</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => { setIsPractice(true); startTest(); }}
                data-testid="practice-test-btn"
                className="flex-1 min-h-[48px] border-2 border-dashed border-[hsl(var(--primary))]/50 bg-transparent text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 hover:border-[hsl(var(--primary))]"
                variant="outline"
              >
                <BookOpen size={16} className="mr-1.5" />
                Practice
                <span className="ml-1 text-[10px] opacity-60">(not scored)</span>
              </Button>

              <Button
                onClick={() => startTest(true)}
                data-testid="start-test-btn"
                className="flex-1 min-h-[48px] btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
              >
                Start Test
                <ChevronRight size={18} className="ml-1" />
              </Button>
            </div>
            <p className="text-center text-xs text-[hsl(var(--muted-foreground))] mt-3" data-testid="practice-vs-test-note">
              Practice is unlimited and never saved or scored. Start Test records your answers, score and XP permanently.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Quiz Screen
  const currentAnswerKey = `${currentCocktailId}_${currentQuestion?._origIndex}`;
  const selectedAnswer = answers[currentAnswerKey];
  const isCorrect = selectedAnswer?.toLowerCase() === currentQuestion?.correct_answer?.toLowerCase();

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] p-4 md:p-8 pb-24">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Practice Mode Banner */}
        {isPractice && (
          <div className="p-2 rounded-lg border-2 border-dashed border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5 text-center" data-testid="practice-banner">
            <span className="text-sm font-medium text-[hsl(var(--primary))]">Practice Mode — results won't be saved</span>
          </div>
        )}
        {/* Score Progress Bar */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--secondary))]">
          <div className="flex items-center gap-2">
            <Star className="text-[hsl(var(--primary))]" size={20} />
            <span className="font-bold text-lg">{totalScore} pts</span>
          </div>
          <span className="text-sm text-[hsl(var(--muted-foreground))]">
            {correctAnswers} correct
          </span>
        </div>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[hsl(var(--muted-foreground))]">
              Cocktail {currentCocktailIndex + 1} of {totalCocktails}
            </span>
            <span className="text-[hsl(var(--muted-foreground))]" data-testid="overall-question-progress">
              Question {overallQuestionNumber} of {totalQuestionsOverall}
            </span>
          </div>
          <Progress value={((currentCocktailIndex * 100) + ((currentQuestionIndex + 1) / totalQuestionsInCocktail * 100)) / totalCocktails} className="h-2" data-testid="test-progress-bar" />
        </div>

        {/* Cocktail Name */}
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardContent className="p-4 text-center">
            <h2 className="text-2xl font-bold text-[hsl(var(--primary))]">{currentCocktailData?.cocktail_name}</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              {currentCocktailData?.category} • {currentCocktailData?.base_spirit}
            </p>
          </CardContent>
        </Card>

        {/* Question */}
        {currentQuestion && (
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              <div className="mb-4">
                <span className="inline-block px-2 py-1 rounded text-xs bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] capitalize mb-2">
                  {currentQuestion.type}
                </span>
                <h3 className="text-lg font-medium">{currentQuestion.question_text}</h3>
              </div>
              
              <RadioGroup 
                value={selectedAnswer || ""} 
                onValueChange={handleAnswer}
                className="space-y-3"
                disabled={showFeedback}
              >
                {currentQuestion.options.map((opt, idx) => {
                  let optionStyle = 'border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]';
                  
                  if (showFeedback) {
                    if (opt === currentQuestion.correct_answer) {
                      optionStyle = 'border-green-500 bg-green-500/20';
                    } else if (opt === selectedAnswer && opt !== currentQuestion.correct_answer) {
                      optionStyle = 'border-red-500 bg-red-500/20';
                    }
                  } else if (selectedAnswer === opt) {
                    optionStyle = 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10';
                  }
                  
                  return (
                    <div 
                      key={`${opt}-${idx}`} 
                      onClick={() => !showFeedback && handleAnswer(opt)}
                      className={`flex items-center space-x-3 p-4 rounded-lg cursor-pointer transition-all border min-h-[48px] ${optionStyle}`}
                      data-testid={`option-${idx}`}
                    >
                      <RadioGroupItem value={opt} id={`opt-${idx}`} disabled={showFeedback} />
                      <Label htmlFor={`opt-${idx}`} className="text-base cursor-pointer flex-1 font-medium">
                        {opt}
                      </Label>
                      {showFeedback && opt === currentQuestion.correct_answer && (
                        <CheckCircle2 className="text-green-500" size={20} />
                      )}
                      {showFeedback && opt === selectedAnswer && opt !== currentQuestion.correct_answer && (
                        <XCircle className="text-red-500" size={20} />
                      )}
                    </div>
                  );
                })}
              </RadioGroup>

              {!showFeedback && !selectedAnswer && (
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3 text-center" data-testid="select-answer-hint">
                  Select an answer to continue
                </p>
              )}

              {/* Feedback */}
              {showFeedback && (
                <div className={`mt-6 p-4 rounded-lg ${isCorrect ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  <div className="flex items-center gap-2">
                    {isCorrect ? (
                      <>
                        <CheckCircle2 className="text-green-500" size={24} />
                        <span className="text-green-400 font-bold text-lg">Correct! +1 point</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="text-red-500" size={24} />
                        <span className="text-red-400 font-bold text-lg">Incorrect</span>
                      </>
                    )}
                  </div>
                  {!isCorrect && (
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
                      Correct answer: <span className="text-green-400 font-medium">{currentQuestion.correct_answer}</span>
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex gap-3">
          <div className="flex-1" />
          {!showFeedback ? (
            <Button
              onClick={submitAnswer}
              disabled={!selectedAnswer}
              data-testid="check-answer-btn"
              className="min-h-[48px] bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
            >
              Check Answer
            </Button>
          ) : (
            <Button
              onClick={nextQuestion}
              data-testid="next-btn"
              className="min-h-[48px] btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
            >
              {currentQuestionIndex < totalQuestionsInCocktail - 1 ? "Next Question" : "See Results"}
              <ChevronRight size={18} className="ml-1" />
            </Button>
          )}
        </div>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}
